import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const Toast = ({ id, message, type = 'info', duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  const getToastStyles = () => {
    const baseStyles =
      'relative flex items-start space-x-3 max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden';
    const animationStyles = isLeaving
      ? 'transition-all duration-300 ease-in transform translate-x-full opacity-0'
      : `transition-all duration-300 ease-out transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`;

    return `${baseStyles} ${animationStyles}`;
  };

  const getIconAndColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          iconColor: 'text-green-400',
          borderColor: 'border-l-green-400',
        };
      case 'error':
        return {
          icon: XCircleIcon,
          iconColor: 'text-red-400',
          borderColor: 'border-l-red-400',
        };
      case 'warning':
        return {
          icon: ExclamationTriangleIcon,
          iconColor: 'text-yellow-400',
          borderColor: 'border-l-yellow-400',
        };
      case 'info':
      default:
        return {
          icon: InformationCircleIcon,
          iconColor: 'text-blue-400',
          borderColor: 'border-l-blue-400',
        };
    }
  };

  const { icon: Icon, iconColor, borderColor } = getIconAndColors();

  return (
    <div className={getToastStyles()}>
      <div className={`absolute bottom-0 left-0 top-0 w-1 ${borderColor}`} />
      <div className="flex-1 p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={handleClose}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Toast;
